/**
 * useBrand Hook
 * React hook for managing brand-specific UI elements (favicon, title, etc.)
 * Requirements: 4_4, 4_5
 */

import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getCurrentBrand, getBrandConfig, saveBrandToStorage } from '../helpers/brand';
import { set } from 'forepaas/store/memory/action';
import { MEMORY_STORE } from '../helpers/constants/storage';

/**
 * Custom hook to manage brand-specific UI elements
 * @returns {object} { brand, brandConfig, updateBrandFromTenant }
 */
export function useBrand() {
  const dispatch = useDispatch();
  const [brand, setBrand] = useState(getCurrentBrand());
  const [brandConfig, setBrandConfig] = useState(getBrandConfig());

  /**
   * Updates the favicon based on current brand
   */
  const updateFavicon = (faviconPath) => {
    try {
      // Remove existing favicon links
      const existingFavicons = document.querySelectorAll("link[rel*='icon']");
      console.log('updateFavicon: Found', existingFavicons.length, 'existing favicon links');
      existingFavicons.forEach((favicon) => {
        console.log('updateFavicon: Removing:', favicon.href);
        favicon.remove();
      });

      // Add new favicon
      const link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/x-icon';
      link.href = faviconPath;
      document.head.appendChild(link);
      console.log('updateFavicon: Added new favicon link:', link.href);

      // Also update apple-touch-icon
      const appleTouchIcon = document.createElement('link');
      appleTouchIcon.rel = 'apple-touch-icon';
      appleTouchIcon.href = faviconPath;
      document.head.appendChild(appleTouchIcon);
      console.log('updateFavicon: Added apple-touch-icon:', appleTouchIcon.href);
    } catch (error) {
      console.error('Error updating favicon:', error);
    }
  };

  /**
   * Updates the page title based on current brand
   */
  const updatePageTitle = (title) => {
    try {
      document.title = title;
    } catch (error) {
      console.error('Error updating page title:', error);
    }
  };

  /**
   * Updates brand based on tenant data from API
   * Called after successful login
   * @param {object} tenantData - Tenant object with brand field
   * Memoized with useCallback to prevent unnecessary re-renders
   */
  const updateBrandFromTenant = useCallback((tenantData) => {
    try {
      if (tenantData && tenantData.brand) {
        const tenantBrand = tenantData.brand.toLowerCase();

        // Save to storage
        saveBrandToStorage(tenantBrand);

        // Update Redux store so all components can react
        dispatch(set(MEMORY_STORE.CURRENT_BRAND, tenantBrand));

        // Update local state
        setBrand(tenantBrand);

        // Update brand config
        const newConfig = getBrandConfig();

        setBrandConfig(newConfig);

        // Update favicon and title
        updateFavicon(newConfig.faviconPath);
        updatePageTitle(newConfig.pageTitle);

        // Update URL to reflect brand (RoomAI uses /roomai path)
        const pathname = window.location.pathname;
        const origin = window.location.origin;
        const hash = window.location.hash;
        const search = window.location.search;

        if (tenantBrand === 'roomai' && !pathname.includes('/roomai')) {
          // Redirect to /roomai
          // Ensure we don't duplicate slashes
          const newPath = pathname === '/' ? '/roomai' : `/roomai${pathname}`;
          window.location.href = `${origin}${newPath}${search}${hash}`;
        } else if (tenantBrand !== 'roomai' && pathname.includes('/roomai')) {
          // Redirect to remove /roomai
          const newPathname = pathname.replace('/roomai', '');
          // Ensure we have at least a root slash if empty
          const finalPathname = newPathname || '/';
          window.location.href = `${origin}${finalPathname}${search}${hash}`;
        }
      }
    } catch (error) {
      console.error('Error updating brand from tenant:', error);
    }
  }, [dispatch]);

  /**
   * Effect to update UI elements when brand changes
   */
  useEffect(() => {
    const config = getBrandConfig();
    updateFavicon(config.faviconPath);
    updatePageTitle(config.pageTitle);
    setBrandConfig(config);
  }, [brand]);

  /**
   * Effect to sync brand from storage on mount and update Redux
   */
  useEffect(() => {
    const currentBrand = getCurrentBrand();
    if (currentBrand !== brand) {
      setBrand(currentBrand);
      // Also update Redux on mount
      dispatch(set(MEMORY_STORE.CURRENT_BRAND, currentBrand));
    }
  }, [dispatch, brand]);

  return {
    brand,
    brandConfig,
    updateBrandFromTenant,
  };
}

export default useBrand;
